import { Component, ChangeDetectionStrategy, OnDestroy, viewChild, signal, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { ToastrService } from 'ngx-toastr';

import { AuthPageShellComponent } from '../shared/auth-layout/auth-layout.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { API } from 'src/app/shared/constants/APIConstants';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RecaptchaModule, RouterLink, AuthPageShellComponent, SharedModule],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnDestroy {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly captchaRef = viewChild<RecaptchaComponent>('captchaRef');

  protected forgotForm!: UntypedFormGroup;
  protected isLoading = signal<boolean>(false);
  /** Set once the request completes — swaps the form for the generic confirmation message. */
  protected submitted = signal<boolean>(false);

  constructor() {
    this.forgotForm = this.fb.group({
      emailId: ['', [Validators.required, Validators.email]],
    });
  }

  protected onSubmit(): void {
    if (this.isLoading() || this.submitted()) return;
    if (this.forgotForm.invalid) {
      this.toastr.error('Please enter a valid email address');
      return;
    }
    const captcha = this.captchaRef();
    if (captcha) {
      captcha.execute();
    } else {
      this.sendRequest();
    }
  }

  protected onCaptchaResolved(token: string | null): void {
    if (!token) return;
    this.sendRequest();
  }

  protected onCaptchaError(): void {
    this.isLoading.set(false);
    this.toastr.error('reCAPTCHA Timeout - Please check your network or domain configuration');
    this.captchaRef()?.reset();
  }

  private sendRequest(): void {
    this.isLoading.set(true);
    const email = (this.forgotForm.get('emailId')?.value || '').trim();
    this.http.post<{ success: boolean; message: string }>(API.FORGOT_PASSWORD, { email })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // The backend always returns a generic success response, regardless of
        // whether the email matched an account — this is intentional (prevents
        // account/email enumeration). The frontend must not branch on outcome.
        next: (res) => {
          this.isLoading.set(false);
          this.submitted.set(true);
        },
        error: () => {
          // Even on a transport-level failure, avoid a technical error message
          // that could hint at backend state. Show the same generic confirmation.
          this.isLoading.set(false);
          this.submitted.set(true);
        },
      });
  }

  protected onBackToLogin(): void {
    this.router.navigateByUrl('/auth/login');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
